'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Box, Button, Stack, Typography } from '@/primitives';
import { APP_ROUTE_PATHS, PUBLIC_ROUTES } from '@/configs/routes';
import { useAdminGuard } from '@/hooks/useAdminGuard';
import { blue, slate } from '@/lib/colours';
import { FadeInWhenVisible } from './FadeInWhenVisible';
import { landingHero } from './homeLandingContent';
import {
  heroAccentGlow,
  heroSectionBg,
  sectionShell,
} from './homeLandingSectionSx';

export function LandingHero() {
  const { handleClickLink } = useAdminGuard(APP_ROUTE_PATHS.DASHBOARD);

  return (
    <Box
      component="section"
      aria-labelledby="landing-hero-title"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 6, md: 10 },
        pb: { xs: 8, md: 12 },
        ...heroSectionBg,
      }}
    >
      <Box
        aria-hidden
        sx={{
          ...heroAccentGlow,
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      />
      <Box sx={{ ...sectionShell, position: 'relative', zIndex: 1 }}>
        <FadeInWhenVisible>
          <Stack alignItems="center" spacing={3}>
            <Box
              sx={{
                position: 'relative',
                width: { xs: 220, sm: 280, md: 320 },
                height: { xs: 46, sm: 59, md: 68 },
              }}
            >
              <Image
                alt="BreedLOGIC"
                fill
                priority
                src="/images/BreedLogic%20Logo%20Reversed.png"
                style={{ objectFit: 'contain' }}
              />
            </Box>
            <Typography
              color={slate[50]}
              component="h1"
              id="landing-hero-title"
              sx={{
                textAlign: 'center',
                maxWidth: 880,
                letterSpacing: '-0.02em',
              }}
              variant="displaymedium"
            >
              {landingHero.title}
            </Typography>
            <Typography
              color={slate[300]}
              component="p"
              sx={{
                textAlign: 'center',
                maxWidth: 640,
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.6,
              }}
            >
              {landingHero.subtitle}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ pt: 1 }}
            >
              <Button
                component={Link}
                href={PUBLIC_ROUTES.SEARCH}
                size="large"
                sx={{
                  px: 3,
                  py: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  backgroundColor: blue[500],
                  boxShadow: `0 12px 40px -12px ${blue[600]}aa`,
                  '&:hover': { backgroundColor: blue[400] },
                }}
                variant="contained"
              >
                {landingHero.primaryCtaSearch}
              </Button>
              <Button
                component={Link}
                href={PUBLIC_ROUTES.REGISTRY_LANDING}
                size="large"
                sx={{
                  px: 3,
                  py: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderColor: slate[600],
                  color: slate[100],
                  '&:hover': {
                    borderColor: slate[400],
                    backgroundColor: slate[800],
                  },
                }}
                variant="outlined"
                onClick={handleClickLink}
              >
                {landingHero.primaryCtaRegistry}
              </Button>
            </Stack>
          </Stack>
        </FadeInWhenVisible>
      </Box>
    </Box>
  );
}
